// Programmatically links each field's error text to its control so screen
// readers announce *why* a field is invalid, not just that it is.
//
// The screens render an empty `<p class="…-error-text" data-field-error="…">`
// inside the field wrapper and toggle its `hidden`/`textContent` when
// validation fails — but nothing tied that text to the input, so assistive
// tech never read it (WCAG 3.3.1 / 4.1.2). This wires `aria-describedby` from
// the control to the error node and keeps `aria-invalid` in sync with the
// error's visible state. It's done centrally (one observer) rather than at
// every render/drawer/row-add site because error nodes are created in dozens
// of places and at arbitrary times.
//
// No visual effect: aria-describedby/aria-invalid/id are non-presentational.

let counter = 0;

function findControl(errorNode) {
  const wrap = errorNode.closest(".ar-input-spec, .ar-select-spec, .ar-textarea-spec");
  if (!wrap) return null; // group/form-level errors have no single control — skip
  return wrap.querySelector("input, select, textarea");
}

// Active = shown AND carries a message. Error nodes start hidden + empty, so a
// freshly wired field reads as valid until validation populates it.
function isActive(errorNode) {
  return !errorNode.hidden && (errorNode.textContent || "").trim() !== "";
}

function refreshInvalid(errorNode, control) {
  control.setAttribute("aria-invalid", isActive(errorNode) ? "true" : "false");
}

function wireNode(errorNode) {
  const control = findControl(errorNode);
  if (!control) return;

  if (!errorNode.id) errorNode.id = `field-error-${++counter}`;

  const tokens = (control.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
  if (!tokens.includes(errorNode.id)) {
    tokens.push(errorNode.id);
    control.setAttribute("aria-describedby", tokens.join(" "));
  }

  refreshInvalid(errorNode, control);
}

function wireWithin(node) {
  if (!(node instanceof Element)) return;
  if (node.matches("[data-field-error]")) wireNode(node);
  node.querySelectorAll("[data-field-error]").forEach(wireNode);
}

let started = false;

export function initFieldErrorA11y(root) {
  if (started) return;
  started = true;

  const target = root || document.getElementById("app") || document.body;

  wireWithin(target);

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "childList") {
        record.addedNodes.forEach(wireWithin);
      } else if (record.type === "attributes") {
        const node = record.target;
        if (node instanceof Element && node.matches("[data-field-error]")) {
          const control = findControl(node);
          if (control) refreshInvalid(node, control);
        }
      }
    }
  });

  observer.observe(target, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["hidden"]
  });
}

// Bring the first invalid field into view after a failed submit.
//
// Validation marks every bad field at once, but the compose form is taller
// than its scroll container — so pressing Send or Schedule send near the
// bottom set an error the user never saw and the form appeared to do nothing.
//
// Reuses the same `isActive` definition the a11y wiring uses (shown AND
// carrying a message), so "first error" means the same thing to both. Scrolls
// the field to the middle of the scroller rather than its top, which keeps it
// clear of the sticky topbar without needing to know the topbar's height.
export function scrollToFirstFieldError(root = document) {
  // Rendered, not just active: the collapsible blocks (discount, deposit,
  // payment plan) keep their error nodes in the DOM while hidden. One left set
  // on a collapsed field would otherwise be picked as "the first error" and
  // scrolled to — which does nothing, and skips the real visible one below it.
  const nodes = Array.from(root.querySelectorAll("[data-field-error]"));
  const first = nodes.find((node) => isActive(node) && node.getClientRects().length > 0);
  if (!first) return false;

  // Prefer the field wrapper so the label and control scroll into view
  // together, not just the error line under them.
  const field = first.closest(".ar-input-spec, .ar-select-spec, .ar-textarea-spec, .ar-dropdown-select") || first;
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  field.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });

  // Focus the control so keyboard and screen-reader users land on the problem
  // too, without fighting the scroll we just started.
  //
  // findControl deliberately only knows about native inputs, and the custom
  // dropdowns back theirs with `input[type=hidden]` — which can't take focus.
  // Fall back to the first genuinely focusable thing in the field, which for
  // those is the trigger button.
  const control = findControl(first);
  const focusable = control && control.type !== "hidden" && control.offsetParent !== null
    ? control
    : field.querySelector('button, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
  if (focusable && typeof focusable.focus === "function") {
    focusable.focus({ preventScroll: true });
  }
  return true;
}
